import * as Sentry from "@sentry/browser";
import { get as _get} from "lodash";

export const SERVICE_MONITORING = 'monitoring';
export const CATEGORY_ANALYTICS = 'analytics';

export function getSentryConfig(i18n) {
    const lang = i18n.locale;
    const labels = {
        [lang]: i18n.t('tekst Monitorowanie błędów Sentry (cookie consent)'),
    };

    return {
        category: CATEGORY_ANALYTICS,
        service: SERVICE_MONITORING,
        config: {
            label: labels[lang] || labels['en']
        },
        cookieTable: {
            body: [
                {
                    name: "sentry-sc",
                    domain: "Sentry",
                    description: lang === 'pl' ? "Plik cookie ustawiony przez Sentry do śledzenia sesji i monitorowania błędów w czasie rzeczywistym" :
                                 lang === 'ua' ? "Файл cookie, встановлений Sentry для відстеження сесії та моніторингу помилок у реальному часі" :
                                 "Cookie set by Sentry for session tracking and real-time error monitoring",
                    expiration: lang === 'pl' ? "Sesja" :
                                lang === 'ua' ? "Сесія" :
                                "Session"
                }
            ]
        },
        autoClear: {
            cookies: [
                { name: 'sentry-sc' },
                { name: /^_sentry_/ }
            ]
        }
    };
}

const STACK_OVERFLOW_MESSAGE = 'Maximum call stack size exceeded';

/**
 * Czy w stosie zdarzenia jest choć jedna ramka z pliku .js, który serwujemy.
 * Kod wstrzyknięty przez przeglądarkę nie pochodzi z żadnego pliku — jego ramki
 * silnik przypisuje do adresu dokumentu.
 */
function hasFrameFromScriptFile(event) {
    const values = event?.exception?.values ?? [];

    return values.some((value) => (value?.stacktrace?.frames ?? []).some(
        (frame) => typeof frame?.filename === 'string' && /\.m?js([?#]|$)/.test(frame.filename)
    ));
}

/**
 * Chrome na iOS i aplikacja Google wstrzykują na stronę własne, zminifikowane skrypty
 * (__gCrWeb: autouzupełnianie, menedżer haseł, tłumaczenie). Kiedy przepełnią stos,
 * wyjątek łapie nasz globalny onerror i leci do Sentry z ramkami przypisanymi do adresu
 * dokumentu — wygląda jak rekursja w panelu, a jest rekursją przeglądarki.
 *
 * Dowód (Sentry, 90 dni, message:"Maximum call stack size exceeded"): 270/270 zdarzeń
 * z Chrome Mobile iOS i aplikacji Google, zero z Mobile Safari — a to ten sam silnik
 * WebKit, ten sam limit stosu i te same strony. Naszego kodu w tym cyklu nie ma.
 *
 * Odrzucamy więc wyłącznie przepełnienie stosu bez ani jednej ramki z naszego pliku .js.
 * Gdyby stos przepełnił nasz kod, w ramkach będzie plik z bundla i zdarzenie przejdzie.
 */
function isStackOverflowOutsideOurScripts(event) {
    const isStackOverflow = (event?.exception?.values ?? []).some(
        (value) => typeof value?.value === 'string' && value.value.includes(STACK_OVERFLOW_MESSAGE)
    );

    return isStackOverflow && !hasFrameFromScriptFile(event);
}

/**
 * Przeglądarki na iOS, które wstrzykują w stronę własne skrypty: Chrome (CriOS) i aplikacja Google
 * (GSA) — te same skrypty co wyżej — oraz Edge (EdgiOS). Znacznik GSA dokleja też aplikacja Google
 * na Androidzie, stąd osobny warunek urządzenia.
 */
const IOS_INJECTING_BROWSER = /\b(CriOS|GSA|EdgiOS)\//;
const IOS_DEVICE = /\b(iPhone|iPad|iPod)\b/;

/**
 * Nieobsłużone odrzucenie obietnicy ze skryptu wstrzykniętego przez przeglądarkę na iOS
 * (SON-FMM, SON-FS6: „Error: La"; SON-FGJ: goły obiekt `{ details, message, status }`).
 * Ramki wskazują adres dokumentu albo ich nie ma, bo ten kod nie pochodzi z pliku.
 *
 * Dowód (Sentry, 90 dni, error.value:La): 18/18 zdarzeń z Chrome Mobile iOS i aplikacji Google,
 * pierwsze 2026-09-03, zero z Safari i z Androida; identyczna pozycja ramek (194:41, 195:338)
 * w panelu najemcy, na /step-up i w osobnej aplikacji podpis.systemobsluginajmu.pl.
 * Dowód SON-FGJ (Sentry, 90 dni): 6/6 zdarzeń z Edge na iOS (EdgiOS 151 i 153), wyłącznie /login,
 * /step-up i /password/reset, zero z Safari na tych stronach (ten sam silnik WebKit); skrypty
 * stron logowania (theme/js/app.js, FingerprintJS z własnym .catch) nie odrzucają gołych obiektów.
 *
 * Nazwa błędu jest zminifikowana i zmienia się z wersją przeglądarki, więc nie filtrujemy po niej.
 * Odrzucamy tylko odrzucenia obietnic z tych przeglądarek bez ani jednej ramki z naszego pliku .js;
 * błąd naszego skryptu wpisanego w stronę nadal przyjdzie z Safari, Androida i komputerów.
 */
function isRejectionFromIosBrowserInjectedScript(event) {
    const isUnhandledRejection = (event?.exception?.values ?? []).some(
        (value) => value?.mechanism?.type === 'onunhandledrejection'
    );

    return isUnhandledRejection
        && IOS_INJECTING_BROWSER.test(window.navigator.userAgent)
        && IOS_DEVICE.test(window.navigator.userAgent)
        && !hasFrameFromScriptFile(event);
}

/**
 * Nieobsłużone odrzucenie obietnicy z dostawcy JSON-RPC, którego w stronę wstrzykuje rozszerzenie
 * przeglądarki (portfele kryptowalut) — SON-FB4: `{ code: -32603, message: 'Internal JSON-RPC error.' }`.
 * W naszym kodzie, zbudowanych zasobach i zależnościach nie ma JSON-RPC; zdarzenia nie mają ramek.
 *
 * Odrzucamy tylko goły obiekt (nie Error) z kodem z zakresu zarezerwowanego przez JSON-RPC 2.0
 * i tylko bez ramki z naszego pliku .js — wyjątek rzucony przez nasz kod zawsze przejdzie.
 */
const JSON_RPC_RESERVED_CODE_MIN = -32768;
const JSON_RPC_RESERVED_CODE_MAX = -32000;

function isRejectionFromInjectedJsonRpcProvider(event, error) {
    const isUnhandledRejection = (event?.exception?.values ?? []).some(
        (value) => value?.mechanism?.type === 'onunhandledrejection'
    );
    const isBareJsonRpcError = !!error
        && typeof error === 'object'
        && !(error instanceof Error)
        && Number.isInteger(error.code)
        && error.code >= JSON_RPC_RESERVED_CODE_MIN
        && error.code <= JSON_RPC_RESERVED_CODE_MAX
        && typeof error.message === 'string';

    return isUnhandledRejection && isBareJsonRpcError && !hasFrameFromScriptFile(event);
}

/**
 * Widgety innych firm ładowane na nasze strony z własnym bundlem. Ich nieobsłużone błędy
 * (np. timeout żądań HelpScout Beacon do beaconapi.helpscout.net — SON-FRD) łapie nasz
 * globalny handler, choć naszego kodu w stosie nie ma. Tak samo skrypty `iabjs://`, które przeglądarka
 * wbudowana Instagrama/Facebooka na Androidzie wstrzykuje w każdą stronę (SON-FE9).
 */
const THIRD_PARTY_SCRIPT_URL = /^(https:\/\/beacon-v2\.helpscout\.net\/|iabjs:\/\/)/;

/**
 * Czy cały stos zdarzenia pochodzi ze skryptu widgetu innej firmy. Zdarzenie bez ramek
 * albo z choć jedną ramką spoza widgetu przechodzi.
 */
function isErrorFromThirdPartyScriptOnly(event) {
    const frames = (event?.exception?.values ?? []).flatMap((value) => value?.stacktrace?.frames ?? [])
        .filter((frame) => typeof frame?.filename === 'string');

    return frames.length > 0 && frames.every((frame) => THIRD_PARTY_SCRIPT_URL.test(frame.filename));
}

/**
 * Środowisko podaje backend metatagiem (layouts/common/formats.blade.php). Bez tego Sentry
 * domyślnie tagował KAŻDE zdarzenie jako "production" i błędy z maszyn deweloperskich
 * zalegały w produkcyjnej kolejce — widać to po trace'ach, w których event frontowy miał
 * environment: production, a backendowy z tego samego żądania environment: local.
 * Tryb budowania (import.meta.env.MODE) tu nie wystarczy: developer buduje assety tą samą
 * komendą co CI, więc też dostałby "production".
 */
function resolveEnvironment() {
    return document.querySelector('meta[name="app-environment"]')?.content || 'unknown';
}

export default function initializeSentry() {
    if (window.scriptsLoaded.sentry || !import.meta.env.VITE_SENTRY_DSN) return;

    const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
    const currentUser = typeof AuthUser !== 'undefined' ? AuthUser : {};
    const sentry_session_replay = _get(currentUser, 'instance.sentry_session_replay');
        Sentry.init({
            dsn: sentryDsn,
            environment: resolveEnvironment(),
            integrations: [
                Sentry.browserTracingIntegration(),
                ...(!!sentry_session_replay ? [Sentry.replayIntegration()] : [])
            ],
            tracesSampleRate: 0.2,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            beforeSend(event, hint) {
                const error = hint.originalException;
                if (error && error.response && error.response.headers) {
                    const ignoreSentryHeader = error.response.headers.get('X-Ignore-Sentry');
                    if (ignoreSentryHeader === 'true') {
                        return null;
                    }
                }

                const listOfErrorMessagesToIgnore = [
                    'Request failed with status code 401',
                    'Request failed with status code 403',
                    'Request failed with status code 404',
                    'Request failed with status code 419',
                    'Request failed with status code 422',
                    'Request failed with status code 503'
                ];

                // exception.value to string z komunikatem, nigdy obiekt — sprawdzenie
                // instanceof jQuery.Event miało tu sens zerowy. Zdarzenie jQuery trzeba
                // rozpoznać na surowym wyjątku z hint.originalException (S3-11591).
                if (error instanceof jQuery.Event) {
                    return null;
                }

                if (event.exception && event.exception.values) {
                    for (const exception of event.exception.values) {
                        if (exception.value && listOfErrorMessagesToIgnore.includes(exception.value)) {
                            return null;
                        }
                    }
                }

                if (isStackOverflowOutsideOurScripts(event)) {
                    return null;
                }

                if (isErrorFromThirdPartyScriptOnly(event)) {
                    return null;
                }

                if (isRejectionFromIosBrowserInjectedScript(event)) {
                    return null;
                }

                if (isRejectionFromInjectedJsonRpcProvider(event, error)) {
                    return null;
                }

                return event;
            },
            ignoreErrors: [
                'NavigationDuplicated',
                /Navigation cancelled from/,
                /Redirected when going from/,
                /Navigation aborted from/,
                /Avoided redundant navigation/,
                'Network Error',
                'Request aborted',
                /^TypeError: Failed to fetch dynamically imported module/,
                /^SQLSTATE\[HY000\] \[2002\] Connection refused.*/,
                /^The check named `.*` did not complete.*/
            ]
        });
    window.scriptsLoaded.sentry = true;
}

export const cookieCallbacks = {
    onFirstConsent: (cookie, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    },

    onConsent: (cookie, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    },

    onChange: (cookie, changedCategories, changedServices, utils) => {
        const acceptedServices = Object.values(cookie.services || []).flat();

        if (acceptedServices.includes(SERVICE_MONITORING)) {
            initializeSentry();
        }
    }
}
